/**
 * Grafici — i pezzi visivi delle statistiche: riquadro numerico, barre orizzontali, colonne per
 * giorno. Una sola serie per grafico (il titolo dice che cosa è: niente legenda), un solo colore
 * (il viola della console, verificato col controllo dei colori), segni sottili con l'estremità
 * arrotondata, valori in inchiostro e mai nel colore della barra, dettaglio al passaggio.
 */

import React from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { tokens } from '../../../theme';

const COLORE_SERIE = tokens.color.tierBusiness.main;

/** Un numero intero all'italiana (1.234). */
export function numero(n: number): string {
  return n.toLocaleString('it-IT');
}

export const Riquadro: React.FC<{
  titolo: string;
  valore: string;
  nota?: string;
  evidenza?: boolean;
}> = ({ titolo, valore, nota, evidenza }) => (
  <Paper
    variant="outlined"
    sx={{
      // Sul telefono due per riga: uno per riga, a tutta larghezza, faceva scorrere quattro schermate di numeri.
      p: { xs: 1.5, sm: 2 }, flex: { xs: '1 1 calc(50% - 6px)', sm: '1 1 200px' }, minWidth: { xs: 0, sm: 180 },
      borderColor: evidenza ? COLORE_SERIE : tokens.color.line, bgcolor: tokens.color.paper,
    }}
  >
    <Typography variant="body2" sx={{ color: tokens.color.ash, fontWeight: 600 }}>{titolo}</Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.display, fontSize: { xs: '1.7rem', sm: '2.1rem' }, fontWeight: 600, lineHeight: 1.15,
        color: tokens.color.graphite, fontVariantNumeric: 'tabular-nums', mt: 0.5,
      }}
    >
      {valore}
    </Typography>
    {nota && <Typography variant="caption" sx={{ color: tokens.color.ash }}>{nota}</Typography>}
  </Paper>
);

export interface RigaBarra {
  chiave: string;
  etichetta: string;
  valore: number;
  /** Il testo a destra della barra (es. «12 · 40%»). */
  testoValore: string;
  /** Il dettaglio al passaggio del mouse. */
  dettaglio?: string;
}

/** Barre orizzontali: una riga per voce, la barra lunga quanto il valore sul massimo. */
export const BarreOrizzontali: React.FC<{ righe: RigaBarra[]; vuoto?: string }> = ({ righe, vuoto }) => {
  const massimo = Math.max(1, ...righe.map((r) => r.valore));
  if (righe.length === 0) {
    return <Typography variant="body2" sx={{ color: tokens.color.mist }}>{vuoto ?? 'Nessun dato.'}</Typography>;
  }
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(140px, 38%) 1fr', columnGap: 1.5, rowGap: 0.75, alignItems: 'center' }}>
      {righe.map((r) => (
        <React.Fragment key={r.chiave}>
          <Typography component="dt" variant="body2" sx={{ color: tokens.color.graphite, overflowWrap: 'anywhere', lineHeight: 1.3 }}>
            {r.etichetta}
          </Typography>
          <Box component="dd" sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Tooltip title={r.dettaglio ?? `${r.etichetta}: ${r.testoValore}`} placement="top" arrow>
              {/* Il bersaglio del passaggio è tutta la riga, non solo la barra (anche a zero). */}
              <Box sx={{ flex: 1, minWidth: 0, py: 0.75, cursor: 'default' }}>
                <Box
                  sx={{
                    height: 14,
                    width: `${(r.valore / massimo) * 100}%`,
                    minWidth: r.valore > 0 ? 4 : 0,
                    bgcolor: COLORE_SERIE,
                    borderRadius: '0 4px 4px 0',
                  }}
                />
              </Box>
            </Tooltip>
            <Typography variant="body2" sx={{ color: tokens.color.ash, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', minWidth: 72, textAlign: 'right' }}>
              {r.testoValore}
            </Typography>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

/** Colonne per giorno: l'andamento degli iscritti, col dettaglio del giorno al passaggio. */
export const ColonneGiornaliere: React.FC<{
  giorni: Array<{ giorno: string; valore: number; dettaglio: string }>;
}> = ({ giorni }) => {
  if (giorni.length === 0) return <Typography variant="body2" sx={{ color: tokens.color.mist }}>Nessun iscritto nel periodo.</Typography>;
  const massimo = Math.max(1, ...giorni.map((g) => g.valore));
  const ALTEZZA = 120;
  const etichetta = (g: string) => {
    const [, m, d] = g.split('-');
    return d && m ? `${d}/${m}` : g;
  };
  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" sx={{ height: ALTEZZA + 20, gap: '2px', borderBottom: `1px solid ${tokens.color.line}`, overflowX: 'auto', pb: 0 }}>
        {giorni.map((g) => (
          <Tooltip key={g.giorno} title={g.dettaglio} placement="top" arrow>
            <Box sx={{ flex: '1 0 14px', maxWidth: 32, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', cursor: 'default' }}>
              <Box sx={{ width: '100%', maxWidth: 24, height: `${(g.valore / massimo) * ALTEZZA}px`, minHeight: g.valore > 0 ? 4 : 0, bgcolor: COLORE_SERIE, borderRadius: '4px 4px 0 0' }} />
            </Box>
          </Tooltip>
        ))}
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: tokens.color.ash }}>{etichetta(giorni[0].giorno)}</Typography>
        <Typography variant="caption" sx={{ color: tokens.color.ash }}>
          picco {numero(massimo)} in un giorno
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.color.ash }}>{etichetta(giorni[giorni.length - 1].giorno)}</Typography>
      </Stack>
    </Box>
  );
};
